import React, {
  Dispatch, ReactElement, SetStateAction, useEffect, useState,
} from 'react';
import {
  StyleSheet, Text, Pressable, View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import TemplateIcon from '../40pxIcons/TemplateIcon';
import AlertModal from '../AlertModal';
import AddTemplateModal from './AddTemplateModal';
import deleteTemplate from '../../requests/Assets/DeleteTemplate';
import { logEvent } from '../LogEvent';
import Style from '../../constants/Style';
import {
  UserPermission, Template, TemplateGroup, TemplateIssue, TemplateType, TemplateTypeTitles,
  UserGroup,
} from '../../constants/Types';
import SensorIcon24 from '../24pxIcons/SensorIcon24';
import MeterIcon from '../24pxIcons/MeterIcon';
import { DELETE_TEMPLATE_FAILURE } from '../../constants/AlertMessages';
import UserIcon24 from '../24pxIcons/UserIcon24';
import { AppScreen } from '../../navigation/LinkingConfiguration';
import PressableStyled, { PressableState } from '../PressableStyled';
import EllipsisIconVertical from '../EllipsisIconVertical';
import getAssets from '../../requests/Assets/GetAssets';
import isMobile from '../IsMobile';
import EllipsisIcon from '../EllipsisIcon';
import SelectionModal from '../SelectionModal';
import AlertIconMobile from '../16pxIcons/AlertIconMobile';
import Button from '../Button';

interface TemplateRowWebProps {
  template: Template,
  closeMenu: () => void,
  showParentAlert,
  deleteAction,
  update,
  templateGroups: TemplateGroup[],
  userGroups: UserGroup[],
  setSelectedTitle: () => void,
  setSelected: () => void,
  setLinkAssetsView: () => void,
  index: number,
  optionsMenuTemplateId?: string,
  setOptionsMenuTemplateId?: Dispatch<SetStateAction<string | null>>,
  // eslint-disable-next-line no-unused-vars
  onViewDetails?: (template: Template) => void,
}

export default function TemplateRow({
  template, closeMenu, showParentAlert, deleteAction, update, templateGroups, setSelected, index,
  userGroups, optionsMenuTemplateId, setOptionsMenuTemplateId, onViewDetails,
}: TemplateRowWebProps) {
  const navigation = useNavigation();
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canEditTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_UPDATE);
  const canDeleteTemplate: boolean = isAdmin
    || permissions?.includes(UserPermission.TEMPLATES_DELETE);
  const optionsEnabled: boolean = !!onViewDetails || canEditTemplate || canDeleteTemplate;
  const buildingId: string = useSelector((state: { selectedBuilding: { id: string } }) => state
    .selectedBuilding.id);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [alertError, setAlertError] = useState<boolean>(true);
  const [alertTitle, setAlertTitle] = useState<string>('');
  const [alertMessage, setAlertMessage] = useState<string | ReactElement>('');
  const [alertAction, setAlertAction] = useState<() => void>(null);
  const [alertActionTitle, setAlertActionTitle] = useState<string>(null);
  const [assetCount, setAssetCount] = useState<number | null>(null);
  const [deleteConfirmationShown, setDeleteConfirmationShown] = useState<boolean>(false);
  const optionsShown: boolean = optionsMenuTemplateId === template.id;

  useEffect(() => {
    setAssetCount(null);
    getAssets({
      templateId: template.id,
      locationId: buildingId,
      enableSubLocationsLookUp: true,
    }).then((res) => setAssetCount(res.assets.length)).catch(() => setAssetCount(null));
  }, [buildingId]);

  const showAlert = (
    title: string,
    message: string | ReactElement,
    error: boolean = true,
    action: () => void = null,
    actionTitle: string = '',
  ) => {
    setAlertTitle(title);
    setAlertMessage(message);
    setAlertError(error);
    setAlertAction(() => action);
    setAlertActionTitle(actionTitle);
    setLoading(false);
    setAlertVisible(true);
  };

  const deleteError = (text?: string) => showAlert(
    'Error',
    text ?? DELETE_TEMPLATE_FAILURE,
  );

  const deletePress: () => void = () => {
    if (isMobile) {
      setDeleteConfirmationShown(true);
      return;
    }
    closeMenu();
    logEvent('TemplateGroupRowWeb', 'Delete');
    showAlert(
      'Delete template',
      <>
        {'Are you sure you want to delete the template '}
        <Text style={Style.alertBoldText}>{template.name}</Text>
      </>,
      true,
      attemptDeletion,
      'Delete',
    );
  };

  const attemptDeletion: () => void = () => {
    setLoading(true);
    deleteTemplate(template.id).then((res: any) => {
      if (res && !res.isError) {
        showParentAlert(
          'Template successfully deleted',
          <Text>
            <Text style={Style.alertBoldText}>{template.name}</Text>
            {' was successfully deleted'}
          </Text>,
          false,
        );
        deleteAction(template.id);
      } else deleteError(res?.errorText);
    }).catch((error) => deleteError(error?.message))
      .finally(() => setLoading(false));
  };

  const showOptionsMenu: () => void = () => {
    setOptionsMenuTemplateId(optionsMenuTemplateId === template.id ? null : template.id);
  };

  const renderIcon = (type: TemplateType) => {
    if (type === TemplateType.SENSOR) return <SensorIcon24 />;
    if (type === TemplateType.METER) {
      return <MeterIcon stroke={isMobile ? Colors.light.hereworksBlue200 : '#fff'} />;
    }
    return <TemplateIcon
      fill={isMobile ? Colors.light.hereworksBlue50 : Colors.light.hereworksDarkBlue}
      stroke={isMobile ? Colors.light.hereworksBlue200 : '#fff'}
    />;
  };

  const responsibleGroupIds: string[] = template.issues
    .reduce((all: string[], currentIssue: TemplateIssue) => {
      all.push(...currentIssue.UserGroupId);
      return all;
    }, []);
  const responsibleUserGroups: UserGroup[] = userGroups
    .filter((userGroup: UserGroup) => responsibleGroupIds.includes(userGroup.UserGroupId));
  const displayedUserGroups: UserGroup[] = responsibleUserGroups.slice(0, 3);

  const goToUserGroup: () => void = () => {
    navigation.navigate(AppScreen.USER_MANAGEMENT as any);
  };

  const restoreDefaults: () => void = () => {
    setEditModalVisible(false);
    setDeleteConfirmationShown(false);
  };

  const showAssets: () => void = () => {
    navigation.navigate(
      AppScreen.ASSET_MANAGEMENT as any,
      { templateId: template.id, templateName: template.name },
    );
  };

  const Menu = () => <View>
    {optionsShown && <View style={styles.optionList}>
      {!!onViewDetails && <Pressable
        style={({ hovered, pressed }: PressableState) => [
          styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        ]}
        onPress={() => {
          closeMenu();
          onViewDetails(template);
        }}
        testID='view-template-details-btn'
      >
        <Text
          style={styles.optionText}
          selectable={false}
        >
          {'View details'}
        </Text>
      </Pressable>}
      {canEditTemplate && <Pressable
        style={({ hovered, pressed }: PressableState) => [
          styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        ]}
        onPress={setSelected}
        testID='am-edit-template-btn'
      >
        <Text
          style={styles.optionText}
          selectable={false}
        >
          {'Edit'}
        </Text>
      </Pressable>}
      {canDeleteTemplate && <Pressable
        style={({ hovered, pressed }: PressableState) => [
          styles.option, hovered && Style.listItemHovered, pressed && Style.listItemPressed,
        ]}
        onPress={deletePress}
        testID='am-delete-template-btn'
      >
        <Text
          style={styles.optionText}
          selectable={false}
        >
          {'Delete'}
        </Text>
      </Pressable>}
    </View>}
  </View>;

  return <View
    testID='template-row'
    style={[
      styles.container,
      { zIndex: index * -1 },
      !isMobile && index % 2 === 0 && styles.containerAlternate,
    ]}
  >
    <View style={styles.header}>
      <View style={styles.icon}>
        {renderIcon(template.type)}
      </View>
      <View style={styles.nameSection}>
        {onViewDetails ? <Pressable
          accessibilityRole='button'
          onPress={() => onViewDetails(template)}
          style={styles.titleButton}
          testID='view-template-details-name-btn'
        >
          {({ hovered }: PressableState) => <Text
            testID='template-row-name'
            style={[styles.title, hovered && styles.titleHovered]}
          >
            {template.name}
          </Text>}
        </Pressable> : <Text testID='template-row-name' style={styles.title}>{template.name}</Text>}
        <Text style={styles.subtitle}>
          {isMobile ? `${assetCount ?? '-'} asset${assetCount === 1 ? '' : 's'}`
            : TemplateTypeTitles[template.type] ?? TemplateTypeTitles[TemplateType.ASSET]}
        </Text>
      </View>
      {isMobile && <PressableStyled
        style={styles.optionsButton}
        onPress={showOptionsMenu}
      >
        <EllipsisIcon />
      </PressableStyled>}
    </View>
    {false && <View style={styles.userGroups}>
      {displayedUserGroups.map((group: UserGroup, groupIndex: number) => <Pressable
        onPress={goToUserGroup}
        style={styles.tag}
        key={groupIndex}
      >
        <UserIcon24 />
        <Text style={styles.tagText}>{group.Name}</Text>
      </Pressable>)}
      {responsibleUserGroups.length > displayedUserGroups.length
        && <View style={styles.tag}>
          <Text style={styles.tagText}>
            {`+${responsibleUserGroups.length - displayedUserGroups.length}`}
          </Text>
        </View>}
    </View>}
    {!isMobile && <View style={styles.countSection}>
      <PressableStyled
        onPress={showAssets}
        style={{ alignSelf: 'flex-start' }}
        testID='navigate-to-assets-btn'
      >
        <Text style={styles.count}>
          {assetCount ?? '-'}
        </Text>
      </PressableStyled>
    </View>}
    <View style={styles.actions}>
      {!isMobile && optionsEnabled && <>
        <PressableStyled
          onPress={showOptionsMenu}
          style={styles.optionsButton}
          testID='template-menu-button'
        >
          <EllipsisIconVertical />
        </PressableStyled>
        <Menu />
      </>}
    </View>
    <AddTemplateModal
      templateGroups={templateGroups}
      isVisible={editModalVisible}
      hide={() => setEditModalVisible(false)}
      templateToUpdate={template}
      update={update}
      showParentAlert={showParentAlert}
    />
    <AlertModal
      modalVisible={alertVisible}
      title={alertTitle}
      message={alertMessage}
      error={alertError}
      action={alertAction}
      actionTitle={alertActionTitle}
      {...{ loading }}
      setModalVisible={setAlertVisible}
    />
    {isMobile && <SelectionModal
      isVisible={optionsShown}
      hide={closeMenu}
      title={template.name}
      onModalHide={restoreDefaults}
      onModalWillShow={restoreDefaults}
      listContent={deleteConfirmationShown ? <>
        {deleteConfirmationShown && <View style={styles.deleteConfirmation}>
          <View style={styles.deleteIconContainer}>
            <AlertIconMobile />
          </View>
          <Text
            style={[styles.deleteConfirmationTitle, { color: styles.deleteButton.borderColor }]}
          >
            {'Delete template?'}
          </Text>
          <Text style={styles.deleteConfirmationSubtitle}>
            {`Are you sure you want to delete the template ${template.name}? This action cannot be undone.`}
          </Text>
          <Button
            title='Delete'
            onPress={attemptDeletion}
            style={{ ...styles.button, ...styles.deleteButton }}
            textStyle={{ color: styles.deleteButton.borderColor }}
          />
          <Button
            title='Cancel'
            onPress={closeMenu}
            style={styles.button}
          />
        </View>}
      </> : <Menu />}
      style={{ maxHeight: undefined }}
    />}
  </View>;
}

const styles = StyleSheet.create({
  container: isMobile ? {
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 10,
    borderColor: Colors.light.hereworksBlue200,
    borderWidth: 1,
    marginBottom: 8,
  } : {
    ...Style.row,
    height: 60,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksBlue100,
  },
  containerAlternate: {
    backgroundColor: Colors.light.hereworksWhite300,
  },
  icon: {
    marginStart: isMobile ? undefined : 18,
    marginEnd: 12,
    width: isMobile ? 48 : 40,
    aspectRatio: 1,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isMobile ? Colors.light.hereworksBlue50 : Colors.light.hereworksDarkBlue,
  },
  nameSection: {
    flex: 1,
    justifyContent: 'center',
  },
  title: isMobile ? {
    fontSize: 14,
    fontFamily: 'Poppins_600SemiBold',
    letterSpacing: 0,
    color: Colors.light.hereworksBlack600,
  } : {
    color: Colors.light.hereworksPrimaryGrey,
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    lineHeight: 24,
  },
  titleButton: {
    alignSelf: 'flex-start',
  },
  titleHovered: {
    color: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
  },
  header: isMobile ? {
    ...Style.row,
  } : {
    flexDirection: 'row',
    flex: 3,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  userGroups: {
    flex: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tag: {
    ...Style.row,
    backgroundColor: Colors.light.hereworksDarkBlue,
    borderRadius: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginEnd: 8,
    marginVertical: 2,
  },
  tagText: {
    color: '#FFF',
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
    lineHeight: 24,
    marginHorizontal: 4,
  },
  optionsButton: isMobile ? {
    alignSelf: 'flex-start',
  } : {
    paddingHorizontal: 16,
  },
  optionList: isMobile ? {} : {
    position: 'absolute',
    ...Style.dropdownMenu,
    end: 16,
    top: 4,
    width: 87,
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: isMobile ? 8 : undefined,
  },
  optionText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  actions: {
    flex: 1,
    alignItems: 'flex-end',
  },
  countSection: {
    flex: 3,
  },
  count: {
    fontSize: 14,
    lineHeight: 18,
    fontFamily: 'Roboto_400Regular',
    letterSpacing: 0,
    color: Colors.light.hereworksBlue500,
    textDecorationLine: 'underline',
  },
  button: {
    alignSelf: 'stretch',
  },
  deleteButton: {
    marginBottom: 12,
    borderColor: Colors.light.hereworksRed500,
  },
  deleteConfirmation: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteConfirmationTitle: {
    fontSize: 22,
    letterSpacing: 0,
    fontFamily: 'Poppins_600SemiBold',
    marginTop: 24,
    marginBottom: 12,
  },
  deleteConfirmationSubtitle: {
    textAlign: 'center',
    fontSize: 14,
    fontFamily: 'Roboto_400Regular',
    color: Colors.light.hereworksBlack400,
    marginBottom: 80,
    letterSpacing: 0,
  },
  deleteIconContainer: {
    width: isMobile ? 40 : 64,
    height: isMobile ? 40 : 64,
    marginTop: 56,
    borderRadius: 32,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.light.hereworksRed500,
  },
});
